import React, { useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useProductConfigs } from '../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { type QuerySuggestion } from '../../utils/third-party-query-suggestions-container/types';
import { IconFor3P } from '../icon-for-3p';

import { messages } from './messages';

const styles = cssMap({
	iconArea: {
		gridArea: 'icon',
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		width: '48px',
	},
	icon: {
		marginBlockStart: token('space.075'),
	},
	pressable: {
		display: 'inline-flex',
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.accent.blue.subtlest'),
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.blue.subtlest.pressed'),
		},
	},
});

type ThirdPartyQuerySuggestionsProps = {
	suggestions: QuerySuggestion[];
	productKey: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	onSuggestionClicked: (suggestion: QuerySuggestion) => void;
	onDismiss: () => void;
	onSuggestionsShown?: () => void;
};

const QuerySuggestion = ({
	suggestion,
	onSuggestionClicked,
}: {
	suggestion: QuerySuggestion;
	onSuggestionClicked: (suggestion: QuerySuggestion) => void;
}) => {
	return (
		<Pressable
			onClick={() => {
				onSuggestionClicked(suggestion);
			}}
			xcss={styles.pressable}
			testId="query-suggestion"
		>
			<SearchIcon label="" color="currentColor" />
			<Text>{suggestion.text}</Text>
		</Pressable>
	);
};

export const ThirdPartyQuerySuggestions = ({
	suggestions,
	productKey,
	thirdPartyConfigs,
	onSuggestionClicked,
	onSuggestionsShown,
	onDismiss,
}: ThirdPartyQuerySuggestionsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const products = useProductConfigs();
	const productName = products[productKey]?.displayName || '';

	useEffect(() => {
		onSuggestionsShown?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Inline space="space.150" testId="third-party-query-suggestions">
			<Box xcss={styles.iconArea}>
				<Box xcss={styles.icon}>
					<IconFor3P
						product={productKey}
						resourceType="document"
						size="medium"
						thirdPartyConfigs={thirdPartyConfigs}
					/>
				</Box>
			</Box>
			<Stack grow="fill" space="space.100">
				<Inline space="space.050" alignBlock="center" spread="space-between">
					<Text size="large" weight="medium">
						<FormattedMessage {...messages.title} values={{ product: productName }} />
					</Text>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} label="" color="currentColor" />}
						onClick={onDismiss}
						label={formatMessage(messages.dismiss)}
						appearance={'subtle'}
						spacing={'compact'}
						testId="third-party-query-suggestions-dismiss"
					/>
				</Inline>
				<Stack space="space.100">
					<Inline space="space.100">
						{suggestions.slice(0, 2).map((suggestion, index) => (
							<QuerySuggestion
								key={index}
								suggestion={suggestion}
								onSuggestionClicked={onSuggestionClicked}
							/>
						))}
					</Inline>
					<Inline space="space.100">
						{suggestions.slice(2, 4).map((suggestion, index) => (
							<QuerySuggestion
								key={index + 2}
								suggestion={suggestion}
								onSuggestionClicked={onSuggestionClicked}
							/>
						))}
					</Inline>
				</Stack>
			</Stack>
		</Inline>
	);
};
